// Vital signs. Recorded by a nurse on the visit, in the Caera app; the office
// can correct an entry but authorship stays with the nurse who took them.
// No automated alerting: the ranges below only shade a reading so a nurse can
// see an outlier at a glance, and are a general paediatric guide, not an order.
import { supabase } from './supabase';

export const TEMP_SITES = ['oral', 'axillary', 'temporal', 'tympanic', 'rectal'];
export const POSITIONS = ['Lying', 'Sitting', 'Standing', 'In wheelchair', 'Held'];
export const ACTIVITIES = ['At rest', 'After activity', 'Asleep', 'Distressed', 'During feed'];

export const FIELDS = [
  { key: 'temperature_f', label: 'Temp',    unit: '°F', step: '0.1', low: 97, high: 100.4 },
  { key: 'pulse',         label: 'Pulse',   unit: 'bpm', low: 60,  high: 140 },
  { key: 'respirations',  label: 'Resp',    unit: '/min', low: 12,  high: 40 },
  { key: 'bp',            label: 'BP',      unit: 'mmHg' },
  { key: 'o2_saturation', label: 'SpO₂',    unit: '%',   low: 92,  high: 100 },
  { key: 'weight_lb',     label: 'Weight',  unit: 'lb', step: '0.1' },
  { key: 'pain_score',    label: 'Pain',    unit: '/10', high: 4 },
];

export const bp = (v) => (v?.bp_systolic ? `${v.bp_systolic}/${v.bp_diastolic ?? '—'}` : null);

export const valueOf = (v, key) => (key === 'bp' ? bp(v) : v?.[key]);

// 'low' | 'high' | null. Presentational only.
export const outOfRange = (v, key) => {
  const f = FIELDS.find(x => x.key === key);
  const n = Number(v?.[key]);
  if (!f || v?.[key] == null || Number.isNaN(n)) return null;
  if (f.low != null && n < f.low) return 'low';
  if (f.high != null && n > f.high) return 'high';
  return null;
};

// FIELDS carries a combined 'bp' entry for display, so read the real columns here.
export const READING_KEYS = ['temperature_f', 'pulse', 'respirations', 'bp_systolic',
  'bp_diastolic', 'o2_saturation', 'weight_lb', 'pain_score'];
export const hasAnyReading = (v) =>
  READING_KEYS.some(k => v?.[k] != null && String(v[k]).trim() !== '');

export const getClientVitals = (clientId, fromIso, toIso) => {
  let q = supabase.from('visit_vitals')
    .select(`id, visit_id, recorded_at, recorded_by_name, temperature_f, temperature_site, pulse,
             respirations, bp_systolic, bp_diastolic, o2_saturation, weight_lb, pain_score,
             activity, position, note, source`)
    .eq('client_id', clientId)
    .order('recorded_at', { ascending: false });
  if (fromIso) q = q.gte('recorded_at', fromIso);
  if (toIso) q = q.lte('recorded_at', toIso);
  return q;
};

export const getVisitVitals = (visitIds) =>
  supabase.from('visit_vitals')
    .select(`id, visit_id, recorded_at, recorded_by_name, temperature_f, temperature_site, pulse,
             respirations, bp_systolic, bp_diastolic, o2_saturation, weight_lb, pain_score,
             activity, position, note, source`)
    .in('visit_id', visitIds)
    .order('recorded_at');

const num = (v) => (v === '' || v == null ? null : Number(v));

export const recordVitals = async (visitId, f) => {
  const { data, error } = await supabase.rpc('record_vitals', {
    p_visit_id: visitId,
    p_temperature_f: num(f.temperature_f), p_temperature_site: f.temperature_site || null,
    p_pulse: num(f.pulse), p_respirations: num(f.respirations),
    p_bp_systolic: num(f.bp_systolic), p_bp_diastolic: num(f.bp_diastolic),
    p_o2_saturation: num(f.o2_saturation), p_weight_lb: num(f.weight_lb),
    p_pain_score: num(f.pain_score), p_activity: f.activity || null,
    p_position: f.position || null, p_note: f.note || null,
  });
  if (error) throw new Error(error.message);
  return data;
};
